import { useState } from "react";
import { supabase } from "../lib/supabase.ts";

// A code, not a link.
//
// Links were the first design, and they could not work for the inbox this was
// first tested on. On 2026-09-16 a one-time sign-in link was fetched twice, 2.4
// seconds apart: first by a click-time link scanner (Windows Chrome, on a
// different network), then by the phone that had actually tapped it. The
// scanner used the token up, so the tap could only fail. Any mail security
// product that checks links when they are clicked does the same, and a
// contractor's inbox is as likely to have one as ours.
//
// A code has no URL to fetch. It also signs in the browser it was typed into,
// whereas a link opens in whatever browser the mail app picks, which on a phone
// is often not the one the observer was using.
//
// Still passwordless: collectors are contractors hired through gig platforms,
// and removing an account is simpler than revoking a password nobody remembers.
export default function Login() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function send(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy(true); setErr(null);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      // Kept so an older email that still carries a link lands on this app
      // rather than on whatever the project's Site URL happens to be.
      options: { emailRedirectTo: window.location.origin },
    });
    setBusy(false);
    if (error) setErr(error.message);
    else { setSent(true); setCode(""); }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(), token: code, type: "email",
    });
    setBusy(false);
    // On success App's auth listener takes over, so there is nothing to do
    // here. On failure, say it in words an observer standing in a food court
    // can act on.
    if (error) {
      setErr(/expired|invalid/i.test(error.message)
        ? "That code is wrong or has expired. Use the newest email, or send a new code."
        : error.message);
    }
  }

  return (
    <div className="pad">
      <p className="brand" style={{ margin: "24px 0 8px" }}>
        <span className="brand-dot" aria-hidden />
        <span className="brand-name">Reset</span>
      </p>
      <h1>Field Observation</h1>
      <p className="muted">Sign in with the email your admin added.</p>
      {!sent ? (
        <form onSubmit={send}>
          <label htmlFor="email">Email</label>
          <input id="email" type="email" required autoComplete="email"
                 inputMode="email" value={email}
                 onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          <button className="primary" disabled={busy || !email.trim()}>
            {busy ? "Sending..." : "Send me a code"}
          </button>
          {err && <p className="err">{err}</p>}
        </form>
      ) : (
        <form onSubmit={verify}>
          <p>We sent a code to <strong>{email.trim()}</strong>.</p>
          <label htmlFor="code">Code from the email</label>
          {/* one-time-code lets iOS offer the code from Mail above the
              keyboard. Digits only, because a pasted "123 456" is still the
              code the observer meant. */}
          <input id="code" type="text" required autoComplete="one-time-code"
                 inputMode="numeric" pattern="[0-9]*" maxLength={10}
                 value={code} autoFocus
                 onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                 placeholder="12345678" />
          <button className="primary" disabled={busy || code.length < 6}>
            {busy ? "Checking..." : "Sign in"}
          </button>
          {err && <p className="err">{err}</p>}
          <div className="actions">
            <button type="button" disabled={busy} onClick={() => void send()}>
              Send a new code
            </button>
            <button type="button" disabled={busy}
                    onClick={() => { setSent(false); setCode(""); setErr(null); }}>
              Different email
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
